import FabRatesPanel from "@/components/FabRatesPanel";
import { useLive } from "@/lib/live";
import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Package, Plus, Warning, Pencil, Trash,
  MagnifyingGlass, Sparkle
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import SheetMasterPanel from "@/components/SheetMasterPanel";
import DuplicateItemsDialog from "@/components/DuplicateItemsDialog";
import { sortItems } from "@/lib/itemSort";

const DEFAULT_CATEGORIES = [
  { v: "sheet", label: "Sheet" },
  { v: "hardware", label: "Hardware" },
];
const UNITS = ["piece", "kg", "sheet", "sqft", "meter", "ft"];

/* Quick starter presets so first-run isn't empty */
const PRESETS = [
  { name: "Stainless Steel Sheet 2mm", category: "sheet", unit: "sheet" },
  { name: "Mild Steel Sheet 2mm", category: "sheet", unit: "sheet" },
  { name: "Golden Steel Sheet 1mm", category: "sheet", unit: "sheet" },
  { name: "Sliding Gate Track", category: "hardware", unit: "meter" },
  { name: "Gate Wheel", category: "hardware", unit: "piece" },
  { name: "Flush Bolt", category: "hardware", unit: "piece" },
];

export default function ItemsPage() {
  const { user } = useAuth();
  const [rev, setRev] = useState(0);
  const [tab, setTab] = useState("items");
  const tabs = [["items", "Items"], ...(["admin", "secondary_admin"].includes(user?.role) ? [["fab", "Fabrication Rates"]] : [])];
  return (
    <div className="space-y-5">
      <div>
        <div className="text-xs font-mono-num tracking-widest text-slate-400">// INVENTORY</div>
        <h1 className="font-heading text-3xl font-black text-white">Items</h1>
        <p className="text-slate-400 text-sm mt-1">
          Raw materials, hardware &amp; consumables. Set the correct <b className="text-orange-400">purchase price</b> on each item —
          profit is calculated automatically as <b>sale rate − purchase rate</b> per sqft (or per piece).
        </p>
      </div>
      <div className="flex gap-2" data-testid="items-tabs">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`h-9 px-4 rounded-sm text-xs font-heading tracking-widest border ${tab === k ? "bg-orange-500 text-slate-900 border-orange-400" : "border-slate-600 text-slate-300"}`} data-testid={`items-tab-${k}`}>{l.toUpperCase()}</button>
        ))}
      </div>
      {tab === "fab" ? <FabRatesPanel isAdmin={user?.role === "admin"} /> : <>
        {user?.role === "admin" && <SheetMasterPanel onChanged={() => setRev((r) => r + 1)} />}
        <ItemsList isAdmin={user?.role === "admin"} rev={rev} />
      </>}
    </div>
  );
}

const q2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

/* ---------------- Items list ---------------- */
function ItemsList({ isAdmin, rev }) {
  const [items, setItems] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [lowOnly, setLowOnly] = useState(false);
  const [activeF, setActiveF] = useState("active");   // active | inactive | all
  const [editing, setEditing] = useState(null); // item or "new"
  const [dupOpen, setDupOpen] = useState(false);
  const [ledgerOf, setLedgerOf] = useState(null);   // item whose ledger dialog is open
  const [openingOf, setOpeningOf] = useState(null); // item whose opening dialog is open
  const [busy, setBusy] = useState(false);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);

  // Fetch dynamic categories (seed + any custom category any item currently has).
  const loadCategories = useCallback(async () => {
    try {
      const { data } = await api.get("/items/categories");
      if (Array.isArray(data) && data.length) {
        setCategories(data.filter((c) => c.key !== "gate_and_railing").map((c) => ({ v: c.key, label: c.label })));
      }
    } catch (_e) { /* keep defaults */ }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const addNewCategoryFromFilter = async () => {
    const label = (window.prompt("Naye category ka naam likhiye (e.g., Furniture, Signage, Windows)") || "").trim();
    if (!label) return;
    try {
      const { data } = await api.post("/items/categories", { label });
      toast.success(`Category "${label}" added`);
      await loadCategories();
      setCat(data.key); // auto-select the newly-created category
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const handleFilterChange = (v) => {
    if (v === "__new__") { addNewCategoryFromFilter(); return; }
    setCat(v);
  };

  useLive(() => { load(); });
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (cat !== "all") params.set("category", cat);
      if (q.trim()) params.set("q", q.trim());
      if (lowOnly) params.set("low_stock", "true");
      const { data } = await api.get(`/items?${params.toString()}`);
      setItems(Array.isArray(data) ? data : []);
      setLoadError("");
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || `Items load fail (${e.response?.status || "network"})`;
      setLoadError(msg);
      toast.error(msg);
    }
    finally { setBusy(false); }
  }, [q, cat, lowOnly, rev]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const del = async (it) => {
    if (!window.confirm(`Delete "${it.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/items/${it.id}`);
      toast.success("Item deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addPreset = async (p) => {
    try {
      await api.post("/items", { ...p, purchase_price: 0, sale_price: 0, current_stock: 0, min_stock: 0 });
      toast.success(`Added ${p.name}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const [view, setView] = useState("main");
  const [allItems, setAllItems] = useState([]);
  useEffect(() => { api.get("/items").then(({ data }) => setAllItems(data || [])).catch(() => {}); }, [items]);
  const byId = Object.fromEntries(allItems.map((i) => [i.id, i]));
  const kids = {};
  allItems.forEach((i) => { if (i.master_item_id) (kids[i.master_item_id] = kids[i.master_item_id] || []).push(i); });
  const linkedOld = sortItems(items.filter((i) => i.master_item_id));
  const shown = view === "linked" ? linkedOld : sortItems(items.filter((i) => !i.master_item_id && i.sheet_status !== "to_confirm"
    && (activeF === "all" || (activeF === "active" ? i.is_active !== false : i.is_active === false))));
  const toConfirm = items.filter((i) => i.sheet_status === "to_confirm" && !i.master_item_id);
  const activeItems = items.filter((i) => i.is_active !== false && !i.master_item_id);
  const totalValue = activeItems.reduce((s, i) => s + (Number(i.current_stock || 0) * Number(i.purchase_price || 0)), 0);
  const lowCount = activeItems.filter((i) => Number(i.current_stock || 0) <= Number(i.min_stock || 0)).length;

  return (
    <div className="space-y-4">
      {/* Summary strip */}
      <div className={`grid grid-cols-2 ${isAdmin ? "md:grid-cols-4" : "md:grid-cols-3"} gap-3`}>
        <SummaryCard label="Total Items" value={activeItems.length} tone="cyan" />
        {isAdmin && <SummaryCard label="Stock Value" value={`₹${totalValue.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`} tone="orange" />}
        <SummaryCard label="Low Stock" value={lowCount} tone={lowCount > 0 ? "red" : "emerald"} />
        <button
          onClick={() => setEditing("new")}
          className="surface p-4 rounded-sm border-t-2 border-orange-500 text-left hover:bg-slate-800/60 transition-colors flex items-center justify-between"
          data-testid="add-item-cta"
        >
          <div>
            <div className="text-[10px] font-mono-num tracking-widest text-slate-400">ADD NEW</div>
            <div className="mt-1 text-orange-400 font-heading text-xl font-black">+ Add Item</div>
          </div>
          <Plus size={26} className="text-orange-400" />
        </button>
      </div>

      {isAdmin && (
        <div className="flex justify-end">
          <button type="button" onClick={() => setDupOpen(true)} className="h-9 px-3 border border-amber-500/60 text-amber-200 rounded-sm text-xs font-mono-num tracking-widest" data-testid="open-duplicates-btn">DUPLICATE ITEMS</button>
          <DuplicateItemsDialog open={dupOpen} onClose={() => setDupOpen(false)} onDone={() => load()} />
        </div>
      )}
      {/* Filters */}
      <div className="surface p-4 rounded-sm flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
          <Input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search item or SKU…"
            className="pl-9 bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm"
            data-testid="items-search-input"
          />
        </div>
        <Select value={cat} onValueChange={handleFilterChange}>
          <SelectTrigger className="w-52 bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm" data-testid="items-category-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-600 text-white">
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((c) => (<SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>))}
            <SelectItem value="__new__" className="text-emerald-300 font-mono-num" data-testid="filter-add-category">
              + Add new category…
            </SelectItem>
          </SelectContent>
        </Select>
        <button
          onClick={() => setLowOnly(!lowOnly)}
          className={
            "px-3 py-2 rounded-sm text-xs font-mono-num tracking-widest border h-10 " +
            (lowOnly
              ? "bg-red-500/20 border-red-500 text-red-300"
              : "bg-slate-800/60 border-slate-700 text-slate-400 hover:bg-slate-800")
          }
          data-testid="low-stock-filter"
        >
          <Warning size={12} className="inline mr-1" /> LOW STOCK ONLY
        </button>
        <div className="flex border border-slate-700 rounded-sm overflow-hidden h-10" data-testid="items-active-filter">
          {[["active", "ACTIVE"], ["inactive", "INACTIVE"], ["all", "ALL"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setActiveF(k)} data-testid={`items-active-filter-${k}`}
              className={"px-3 text-xs font-mono-num tracking-widest " + (activeF === k ? "bg-orange-500/20 text-orange-200" : "bg-slate-800/60 text-slate-400 hover:bg-slate-800")}>{l}</button>
          ))}
        </div>
      </div>

      {loadError && (
        <div className="surface p-4 rounded-sm border border-red-500/50 text-red-300 text-sm" data-testid="items-load-error">
          Items load nahi hue: {loadError}. Data safe hai — page refresh karke dobara try karein.
        </div>
      )}
      {/* Empty state with presets */}
      {!busy && !loadError && items.length === 0 && !q.trim() && cat === "all" && !lowOnly && (
        <div className="surface p-8 rounded-sm">
          <div className="flex items-center gap-2 mb-4">
            <Sparkle size={18} className="text-orange-400" weight="fill" />
            <div className="font-heading text-lg text-white">Quick start — add common items</div>
          </div>
          <p className="text-slate-400 text-sm mb-4">
            Tap any suggestion to add it. You can edit prices & stock afterwards.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.name}
                onClick={() => addPreset(p)}
                className="flex items-center gap-2 p-3 rounded-sm surface-2 hover:bg-slate-800 text-left"
                data-testid={`preset-${p.name.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <Plus size={14} className="text-orange-400" />
                <span className="text-slate-200 text-sm">{p.name}</span>
                <span className="ml-auto text-[10px] font-mono-num text-slate-500">{p.unit}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap" data-testid="items-view-tabs">
        {[["main", `Masters & items (${items.filter((i) => !i.master_item_id).length})`], ["linked", `Linked old items (${linkedOld.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} className={`h-8 px-3 rounded-sm text-[10px] font-mono-num tracking-widest border ${view === k ? "bg-cyan-500 text-slate-900 border-cyan-400" : "border-slate-600 text-slate-300"}`} data-testid={`items-view-${k}`}>{l.toUpperCase()}</button>
        ))}
        {toConfirm.length > 0 && view === "main" && (
          <span className="text-[10px] font-mono-num text-amber-300 self-center" data-testid="items-to-confirm">TO CONFIRM (dropdown me chhupe): {toConfirm.map((i) => i.name).join(", ")}</span>
        )}
      </div>
      {/* List */}
      {shown.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {shown.map((it) => {
            const stock = q2(it.current_stock);
            const min = Number(it.min_stock || 0);
            const isLinked = !!it.master_item_id;
            const negative = stock < 0 && it.is_active !== false && !isLinked;
            const chips = kids[it.id] || [];
            const low = stock <= min && min > 0 && !negative;
            const outOfStock = stock === 0;
            const catLabel = categories.find((c) => c.v === it.category)?.label || it.category;
            return (
              <div key={it.id} className={
                "surface p-4 rounded-sm border-l-4 " +
                (negative ? "border-red-500" : outOfStock ? "border-red-500" : low ? "border-amber-500" : "border-slate-700")
              } data-testid={`item-card-${it.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className={"font-semibold truncate " + (it.is_active === false ? "text-slate-500 line-through" : "text-slate-100")}>{it.name}</div>
                      {!isLinked && <ActiveToggle item={it} isAdmin={isAdmin} onChanged={(u) => setItems((p) => p.map((x) => (x.id === u.id ? u : x)))} />}
                      {it.category === "hardware" && (
                        <span className="text-[9px] font-mono-num tracking-widest px-1.5 py-0.5 rounded-sm bg-emerald-500/20 border border-emerald-500/50 text-emerald-300"
                              data-testid={`hardware-badge-${it.id}`}>
                          READY-MADE
                        </span>
                      )}
                      {it.is_service && (
                        <span className="text-[9px] font-mono-num tracking-widest px-1.5 py-0.5 rounded-sm bg-slate-500/20 border border-slate-500/50 text-slate-300" data-testid={`service-badge-${it.id}`}>
                          SERVICE
                        </span>
                      )}
                      {!it.opening_verified && !it.is_service && !isLinked && (
                        <span className="text-[9px] font-mono-num tracking-widest px-1.5 py-0.5 rounded-sm bg-amber-500/20 border border-amber-500/50 text-amber-300" title="Set physical opening stock to enable ledger tracking" data-testid={`opening-not-set-${it.id}`}>
                          OPENING NOT SET
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] font-mono-num text-slate-500 tracking-widest mt-0.5">
                      {catLabel.toUpperCase()} · {it.unit.toUpperCase()}
                      {it.sku && <span className="text-slate-600"> · #{it.sku}</span>}
                    </div>
                  </div>
                  {!isLinked && <div className="flex gap-1">
                    <button onClick={() => setEditing(it)} className="text-slate-500 hover:text-white p-1" title="Edit" data-testid={`item-edit-${it.id}`}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => del(it)} className="text-slate-500 hover:text-red-400 p-1" title="Delete" data-testid={`item-delete-${it.id}`}>
                      <Trash size={14} />
                    </button>
                  </div>}
                </div>

                <div className={`grid ${isAdmin ? "grid-cols-3" : "grid-cols-2"} gap-2 mb-3`}>
                  <MiniStat label="Stock" value={`${stock} ${it.unit}`}
                    tone={negative ? "red" : outOfStock ? "red" : low ? "orange" : "cyan"}
                    testid={`item-stock-${it.id}`}
                  />
                  {isAdmin && <MiniStat label="Cost" value={`₹${it.purchase_price}`} testid={`item-cost-${it.id}`} />}
                  <MiniStat label="Sale" value={`₹${it.sale_price}`} tone="emerald" />
                </div>
                {(Number(it.company_price) > 0 || it.welder_rate_sqft != null || it.job_work_rate_sqft != null) && (
                  <div className="text-[10px] font-mono-num tracking-widest text-amber-300/90 mb-2" data-testid={`item-rates-${it.id}`}>
                    CO ₹{it.company_price || 0}
                    {it.welder_rate_sqft != null && <> · WELDER ₹{it.welder_rate_sqft}</>}
                    {it.job_work_rate_sqft != null && <> · JOB WORK ₹{it.job_work_rate_sqft}</>}
                  </div>
                )}

                {(it.item_type === "SERVICE" || it.metal || it.master_item_id || it.sheet_status) && (
                  <div className="text-[10px] font-mono-num text-slate-400 tracking-widest mb-2" data-testid={`item-sheet-meta-${it.id}`}>
                    {it.item_type === "SERVICE" ? <span className="text-violet-300">SERVICE</span> : (
                      <>{it.metal || "—"} · {it.gauge ? `${it.gauge}G` : it.thickness_mm != null ? `${it.thickness_mm}MM` : "—"}</>
                    )}
                    {it.sheet_status === "to_confirm" && <span className="text-amber-300"> · TO CONFIRM</span>}
                    {it.master_item_id && <span className="text-cyan-300"> · LINKED</span>}
                  </div>
                )}
                {isLinked && (
                  <div className="text-[10px] font-mono-num text-cyan-300 mb-2" data-testid={`item-linked-to-${it.id}`}>LINKED → {byId[it.master_item_id]?.name || "master"} · purana item, stock count nahi hota</div>
                )}
                {chips.length > 0 && (
                  <div className="mb-2 space-y-1" data-testid={`item-chips-${it.id}`}>
                    <div className="flex flex-wrap gap-1 items-center">
                      <span className="text-[9px] font-mono-num text-slate-500">LINKED:</span>
                      {chips.map((c) => (
                        <button key={c.id} onClick={() => setLedgerOf(c)} className="text-[10px] px-1.5 py-0.5 rounded-sm bg-cyan-500/10 border border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/20" data-testid={`item-chip-${c.id}`}>{c.name}</button>
                      ))}
                    </div>
                    <div className="text-[9px] font-mono-num text-slate-500" data-testid={`item-old-linked-balance-${it.id}`}>
                      Old linked balance: {q2(chips.reduce((t, c) => t + Number(c.current_stock || 0), 0))} (not counted)
                    </div>
                  </div>
                )}
                {negative && (
                  <div className="text-[10px] font-mono-num text-red-300 tracking-widest mb-2 flex items-center gap-1" data-testid={`item-negative-${it.id}`}>
                    <Warning size={11}/> NEGATIVE STOCK · SHORT BY {Math.abs(stock)} {it.unit}
                  </div>
                )}
                {low && (
                  <div className="text-[10px] font-mono-num text-amber-400 tracking-widest mb-2 flex items-center gap-1">
                    <Warning size={11}/> LOW STOCK · MIN {min}
                  </div>
                )}

                {!it.is_service && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setLedgerOf(it)}
                      className="flex-1 h-8 rounded-sm bg-slate-800 hover:bg-slate-700 text-slate-200 text-[10px] font-mono-num tracking-widest"
                      data-testid={`item-view-ledger-${it.id}`}
                    >
                      VIEW LEDGER
                    </button>
                    {!isLinked && it.is_active !== false && (!it.opening_verified || isAdmin) && (
                    <button
                      onClick={() => setOpeningOf(it)}
                      className="flex-1 h-8 rounded-sm bg-cyan-600/20 border border-cyan-500/40 hover:bg-cyan-600/30 text-cyan-200 text-[10px] font-mono-num tracking-widest"
                      data-testid={`item-set-opening-${it.id}`}
                    >
                      {it.opening_verified ? "RESET OPENING" : "SET OPENING"}
                    </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Dialogs */}
      <ItemEditor
        isAdmin={isAdmin}
        open={!!editing}
        item={editing === "new" ? null : editing}
        categories={categories}
        onCategoriesRefresh={loadCategories}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); loadCategories(); }}
      />
      <StockLedgerDialog
        item={ledgerOf}
        onClose={() => setLedgerOf(null)}
      />
      <OpeningStockDialog
        item={openingOf}
        onClose={() => setOpeningOf(null)}
        onSaved={() => { setOpeningOf(null); load(); }}
      />
    </div>
  );
}

function SummaryCard({ label, value, tone = "orange" }) {
  const toneMap = {
    orange: "text-orange-400 border-orange-500/40",
    cyan: "text-cyan-300 border-cyan-500/40",
    emerald: "text-emerald-400 border-emerald-500/40",
    red: "text-red-400 border-red-500/40",
  };
  return (
    <div className={`surface p-4 rounded-sm border-t-2 ${toneMap[tone]}`}>
      <div className="text-[10px] font-mono-num tracking-widest text-slate-400">{label.toUpperCase()}</div>
      <div className={`mt-1 font-heading text-2xl font-black font-mono-num ${toneMap[tone].split(" ")[0]}`}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value, tone = "slate", testid }) {
  const toneMap = {
    slate: "text-slate-200",
    cyan: "text-cyan-300",
    orange: "text-orange-400",
    emerald: "text-emerald-400",
    red: "text-red-400",
  };
  return (
    <div className="surface-2 px-2.5 py-2 rounded-sm" data-testid={testid}>
      <div className="text-[9px] font-mono-num text-slate-500 tracking-widest">{label.toUpperCase()}</div>
      <div className={`font-mono-num text-sm font-semibold ${toneMap[tone]}`}>{value}</div>
    </div>
  );
}

/* ---------------- Item editor dialog ---------------- */
function ItemEditor({ isAdmin, open, item, categories = DEFAULT_CATEGORIES, onCategoriesRefresh, onClose, onSaved }) {
  const [form, setForm] = useState(defaultForm());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm(item ? { ...defaultForm(), ...item } : defaultForm());
  }, [open, item]);

  const promptNewCategory = async () => {
    const label = (window.prompt("Naye category ka naam likhiye (e.g., Furniture, Signage)") || "").trim();
    if (!label) return;
    try {
      const { data } = await api.post("/items/categories", { label });
      setForm({ ...form, category: data.key });
      onCategoriesRefresh?.();
      toast.success(`Category "${label}" saved`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const handleCategoryChange = (v) => {
    if (v === "__new__") { promptNewCategory(); return; }
    setForm({ ...form, category: v, unit: !item && v === "sheet" ? "sqft" : form.unit });
  };

  // Include the currently chosen category even if it's not in the fetched list
  // (e.g., freshly typed via + Add new category — item hasn't been saved yet).
  const catOptions = [...categories];
  if (form.category && !catOptions.find((c) => c.v === form.category)) {
    catOptions.push({
      v: form.category,
      label: form.category.replace(/_and_/g, " & ").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    });
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        unit: form.unit,
        purchase_price: Number(form.purchase_price) || 0,
        sale_price: Number(form.sale_price) || 0,
        mrp: Number(form.mrp) || 0,
        company_price: Number(form.company_price) || 0,
        current_stock: Number(form.current_stock) || 0,
        min_stock: Number(form.min_stock) || 0,
        sku: form.sku || "",
        notes: form.notes || "",
        ...(isAdmin ? { display_order: form.display_order === "" || form.display_order == null ? -1 : Number(form.display_order) } : {}),
      };
      if (!isAdmin) delete payload.purchase_price;
      if (form.category === "sheet") {
        payload.metal = form.metal || null;
        for (const k of ["thickness_mm", "gauge", "job_work_rate_sqft", "welder_rate_sqft"]) {
          if (form[k] !== "" && form[k] != null) payload[k] = Number(form[k]);
        }
      }
      if (item?.id) await api.patch(`/items/${item.id}`, payload);
      else await api.post("/items", payload);
      toast.success(item ? "Item updated" : `${payload.name} added`);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg bg-slate-900 border-slate-700 text-white rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-heading">{item ? "Edit Item" : "Add New Item"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="item-editor-form">
          <Field label="Item Name">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., Stainless Steel Sheet 2mm"
              required data-testid="item-name-input"
              className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm"/>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <Select value={form.category} onValueChange={handleCategoryChange}>
                <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-600 text-white">
                  {catOptions.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
                  <SelectItem value="__new__" className="text-emerald-300 font-mono-num">+ Add new category…</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Unit">
              <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-600 text-white">
                  {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          {form.category === "sheet" && <SheetFields form={form} setForm={setForm} isAdmin={isAdmin} />}
          <div className={`grid ${isAdmin ? "grid-cols-2" : "grid-cols-1"} gap-3`}>
            {isAdmin && (
            <Field label="Purchase Price (₹)">
              <Input type="number" step="0.01" min="0"
                value={form.purchase_price}
                onChange={(e) => setForm({ ...form, purchase_price: e.target.value })}
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm font-mono-num"
                data-testid="purchase-price-input"/>
            </Field>
            )}
            <Field label="Sale Price (₹)">
              <Input type="number" step="0.01" min="0"
                value={form.sale_price}
                onChange={(e) => setForm({ ...form, sale_price: e.target.value })}
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm font-mono-num"
                data-testid="sale-price-input"/>
            </Field>
          </div>
          <div className="rounded-sm border border-amber-500/40 bg-amber-500/5 p-3 space-y-3"
               data-testid="commission-fields">
            <div className="flex items-start gap-2">
              <div className="w-1 h-10 bg-amber-500 shrink-0" />
              <div className="flex-1">
                <div className="font-heading text-[11px] font-black tracking-widest text-amber-300">
                  STAFF COMMISSION WINDOW (OPTIONAL)
                </div>
                <div className="text-[10.5px] text-slate-400 mt-0.5 leading-relaxed">
                  Example: MRP ₹250, Company Price ₹200 → sell at ₹220, commission = <b className="text-amber-300">₹20</b>.
                  Sell at ≥₹250 → ₹50 cap. Sell at ≤₹200 → ₹0.
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="MRP (₹)">
                <Input type="number" step="0.01" min="0"
                  value={form.mrp}
                  onChange={(e) => setForm({ ...form, mrp: e.target.value })}
                  placeholder="e.g., 250"
                  className="bg-slate-800/60 border-amber-500/40 text-white h-10 rounded-sm font-mono-num"
                  data-testid="mrp-input"/>
              </Field>
              <Field label="Company rate (₹) — admin only">
                <Input type="number" step="0.01" min="0" disabled={!isAdmin}
                  value={form.company_price}
                  onChange={(e) => setForm({ ...form, company_price: e.target.value })}
                  placeholder="e.g., 200"
                  className="bg-slate-800/60 border-amber-500/40 text-white h-10 rounded-sm font-mono-num"
                  data-testid="company-price-input"/>
              </Field>
            </div>
            {(() => {
              const mrp = parseFloat(form.mrp) || 0;
              const cp = parseFloat(form.company_price) || 0;
              if (mrp <= 0 || cp <= 0) {
                return <div className="text-[10.5px] text-slate-500">Dono values &gt; 0 bharo tabhi commission compute hoga.</div>;
              }
              if (cp >= mrp) {
                return <div className="text-[10.5px] text-red-400 font-mono-num">⚠ Company Price MRP se kam hona chahiye (MRP {mrp} ≤ CP {cp})</div>;
              }
              const maxComm = (mrp - cp).toFixed(2);
              return <div className="text-[10.5px] text-emerald-300 font-mono-num">Max per-unit commission = ₹{maxComm}</div>;
            })()}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Current Stock">
              <Input type="number" step="0.001" min="0"
                value={form.current_stock}
                onChange={(e) => setForm({ ...form, current_stock: e.target.value })}
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm font-mono-num"
                data-testid="current-stock-input"/>
            </Field>
            <Field label="Low Stock Alert Below">
              <Input type="number" step="0.001" min="0"
                value={form.min_stock}
                onChange={(e) => setForm({ ...form, min_stock: e.target.value })}
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm font-mono-num"/>
            </Field>
          </div>
          {isAdmin && (
            <Field label="Display order (optional — chhota number upar)">
              <Input type="number" value={form.display_order ?? ""} onChange={(e) => setForm({ ...form, display_order: e.target.value })}
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm font-mono-num" data-testid="item-display-order-input" />
            </Field>
          )}
          <Field label="SKU (optional)">
            <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })}
              placeholder="Internal code"
              className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm font-mono-num"/>
          </Field>
          {form.category === "hardware" && (
            <div
              className="rounded-sm border-2 border-emerald-500 bg-emerald-500/10 p-3"
              data-testid="hardware-info-banner"
            >
              <div className="font-heading text-sm font-black tracking-wide text-emerald-300">
                HARDWARE ITEM — NO CUTTING
              </div>
              <div className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                Hardware category ka matlab yeh <b>ready stock</b> hai (gate wheel, bolt, track etc.).
                Aisa order jisme <b>sirf hardware items</b> hain, seedha{" "}
                <b className="text-emerald-400">Ready for Delivery</b> me banega — design/cutting skip.
              </div>
            </div>
          )}
          {isAdmin && <div className="text-[11px] text-slate-500 border border-slate-700 rounded-sm p-3 leading-relaxed">
            <b className="text-slate-300">Profit calculation:</b> Har order me automatically{" "}
            <b className="text-emerald-400">Sale Price − Purchase Price</b> per sqft (ya per piece){" "}
            profit maana jayega. Example: Sale ₹250/sqft, Purchase ₹110/sqft → profit ₹140/sqft.
          </div>}
          <Button type="submit" disabled={busy}
            className="w-full btn-industrial rounded-sm h-11" data-testid="save-item-button">
            {busy ? "SAVING…" : (item ? "SAVE CHANGES" : "ADD ITEM")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function defaultForm() {
  return {
    name: "", category: "sheet", unit: "sqft",
    purchase_price: 0, sale_price: 0,
    mrp: 0, company_price: 0,
    current_stock: 0, min_stock: 0,
    sku: "", notes: "",
    metal: "", thickness_mm: "", gauge: "",
  };
}

const METALS = ["MS", "SS", "GOLDEN 202", "GI", "AL", "OTHER"];

function SheetFields({ form, setForm, isAdmin }) {
  const cls = "bg-slate-800/60 border-cyan-500/40 text-white h-10 rounded-sm font-mono-num";
  return (
    <div className="rounded-sm border border-cyan-500/40 bg-cyan-500/5 p-3 space-y-3" data-testid="sheet-fields">
      <div className="font-heading text-[11px] font-black tracking-widest text-cyan-300">SHEET DETAILS</div>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Metal">
          <Select value={form.metal || ""} onValueChange={(v) => setForm({ ...form, metal: v })}>
            <SelectTrigger className={cls} data-testid="sheet-metal-select"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-600 text-white">
              {METALS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Thickness (mm)">
          <Input type="number" step="0.001" min="0" value={form.thickness_mm ?? ""} className={cls}
            onChange={(e) => setForm({ ...form, thickness_mm: e.target.value })} data-testid="sheet-thickness-input" />
        </Field>
        <Field label="Gauge (SS)">
          <Input type="number" step="1" min="0" value={form.gauge ?? ""} className={cls}
            onChange={(e) => setForm({ ...form, gauge: e.target.value })} data-testid="sheet-gauge-input" />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3" data-testid="sheet-admin-rates">
        <Field label="Job Work rate (₹/sqft)">
          <Input type="number" step="0.01" min="0" value={form.job_work_rate_sqft ?? ""} className={cls} disabled={!isAdmin}
            onChange={(e) => setForm({ ...form, job_work_rate_sqft: e.target.value })} data-testid="sheet-job-work-rate-input" />
        </Field>
        <Field label="Welder rate (₹/sqft) — lowest">
          <Input type="number" step="0.01" min="0" value={form.welder_rate_sqft ?? ""} className={cls} disabled={!isAdmin}
            placeholder="e.g. 180"
            onChange={(e) => setForm({ ...form, welder_rate_sqft: e.target.value })} data-testid="sheet-welder-rate-input" />
        </Field>
      </div>
      <div className="text-[10px] text-slate-500">Job Work = customer ki sheet, sirf cutting. Welder rate = selling rate − welder commission isse neeche nahi ja sakta.{!isAdmin && " Sirf main Admin badal sakta hai."}</div>
      <div className="text-[10px] font-mono-num text-slate-500 tracking-widest">
        SHEET WEIGHT: {form.start_sheet_weight_kg ? `${form.start_sheet_weight_kg} KG / ${form.sheet_size_sqft || 32} SQFT (STARTING VALUE)` : "PURCHASE SE CALCULATE HOGA"}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">{label.toUpperCase()}</Label>
      {children}
    </div>
  );
}


/* ---------------- Stock Ledger dialog ---------------- */
function StockLedgerDialog({ item, onClose }) {
  const [ledger, setLedger] = useState(null);
  useEffect(() => {
    if (!item) return setLedger(null);
    (async () => {
      try {
        const { data } = await api.get(`/items/${item.id}/ledger`);
        setLedger(data);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, [item]);
  if (!item) return null;
  const negative = ledger && ledger.current_stock < 0;
  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-3xl" data-testid="stock-ledger-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span>{item.name}</span>
            <span className="text-[10px] font-mono-num tracking-widest text-slate-500">{item.unit?.toUpperCase()}</span>
          </DialogTitle>
        </DialogHeader>
        {!ledger ? (
          <div className="text-slate-500 text-sm">Loading…</div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <MiniStat label="Opening" value={`${q2(ledger.opening_stock)} ${item.unit}`} tone="slate" />
              <MiniStat label="Current" value={`${q2(ledger.current_stock)} ${item.unit}`} tone={negative ? "red" : "cyan"} testid="ledger-current" />
              <MiniStat label="Movements" value={ledger.events.length} tone="slate" />
            </div>
            {negative && (
              <div className="text-[10px] font-mono-num text-red-300 tracking-widest flex items-center gap-1">
                <Warning size={11}/> NEGATIVE STOCK — SHORT BY {Math.abs(q2(ledger.current_stock))} {item.unit}
              </div>
            )}
            <div className="border border-slate-700 rounded-sm overflow-hidden max-h-[60vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-800 text-[10px] font-mono-num tracking-widest text-slate-400 sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left">DATE</th>
                    <th className="px-2 py-1.5 text-left">TYPE</th>
                    <th className="px-2 py-1.5 text-left">REF</th>
                    <th className="px-2 py-1.5 text-right">IN</th>
                    <th className="px-2 py-1.5 text-right">OUT</th>
                    <th className="px-2 py-1.5 text-right">BALANCE</th>
                    <th className="px-2 py-1.5 text-left">NOTE</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.events.map((ev) => (
                    <tr key={ev.id} className="border-t border-slate-800" data-testid={`ledger-event-${ev.id}`}>
                      <td className="px-2 py-1.5 font-mono-num text-[11px] text-slate-500">{(ev.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="px-2 py-1.5 font-mono-num text-[10px] tracking-widest">
                        <span className={
                          ev.direction === "IN"
                            ? "text-emerald-300"
                            : "text-red-300"
                        }>{ev.movement_type}</span>
                      </td>
                      <td className="px-2 py-1.5 text-[11px] text-slate-400">{ev.reference_no || "—"}</td>
                      <td className="px-2 py-1.5 text-right font-mono-num text-emerald-300">{ev.in ? q2(ev.in) : ""}</td>
                      <td className="px-2 py-1.5 text-right font-mono-num text-red-300">{ev.out ? q2(ev.out) : ""}</td>
                      <td className={"px-2 py-1.5 text-right font-mono-num font-bold " + (ev.balance < 0 ? "text-red-400" : "text-slate-200")}>{q2(ev.balance)}</td>
                      <td className="px-2 py-1.5 text-[11px] text-slate-500 truncate max-w-[240px]">{ev.note || ""}</td>
                    </tr>
                  ))}
                  {ledger.events.length === 0 && (
                    <tr><td colSpan="7" className="text-center py-4 text-slate-500 italic text-sm">No movements yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


/* ---------------- Opening Stock dialog ---------------- */
function OpeningStockDialog({ item, onClose, onSaved }) {
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const bySheets = item && String(item.category || "").toLowerCase() === "sheet" && String(item.unit || "").toLowerCase() === "sqft";
  const size = Number(item?.sheet_size_sqft) || 32;
  useEffect(() => {
    if (!item) return;
    setQty("");
    setNote("");
  }, [item]);
  if (!item) return null;
  const save = async () => {
    if (qty === "" || !(Number(qty) >= 0)) return toast.error("Valid quantity daaliye");
    setBusy(true);
    try {
      await api.post(`/items/${item.id}/opening-stock`, bySheets ? { sheets: Number(qty), note } : { opening_stock: Number(qty), note });
      toast.success(bySheets ? `Opening: ${qty} sheets = ${Number(qty) * size} sqft` : `Opening stock set to ${qty} ${item.unit}`);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-md" data-testid="opening-stock-dialog">
        <DialogHeader>
          <DialogTitle>Set Opening Stock — {item.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-[11px] text-slate-400">
            Physical count karo aur exact quantity daalo. Iske baad har order/purchase automatically ledger update karega. Old stock changes ledger balance ko affect nahi karenge — clean opening point ban jayega.
          </div>
          <Field label={bySheets ? `Number of sheets (1 sheet = ${size} sqft) · date: aaj (IST)` : `Opening Stock (${item.unit})`}>
            <Input value={qty} onChange={(e) => setQty(e.target.value)} type="number" step={bySheets ? "1" : "0.001"}
                   className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm font-mono-num"
                   data-testid="opening-stock-input" />
          </Field>
          {bySheets && qty !== "" && <div className="text-sm font-mono-num text-cyan-300" data-testid="opening-sqft-preview">= {Number(qty) * size} sqft</div>}
          {item.opening_verified && <div className="text-[11px] text-amber-300">RESET: purani opening replace hogi (audit log me record).</div>}
          <Field label="Note (optional)">
            <Input value={note} onChange={(e) => setNote(e.target.value)}
                   placeholder="e.g. physical count 22-Aug-2026"
                   className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm" />
          </Field>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onClose} className="rounded-sm h-10">CANCEL</Button>
            <Button onClick={save} disabled={busy} className="btn-industrial rounded-sm h-10" data-testid="opening-stock-save">
              {busy ? "SAVING…" : (item.opening_verified ? "RESET OPENING" : "SET OPENING")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ActiveToggle({ item, isAdmin, onChanged }) {
  const on = item.is_active !== false;
  const cls = "text-[9px] font-mono-num tracking-widest px-1.5 py-0.5 rounded-sm border " +
    (on ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-300" : "bg-slate-600/20 border-slate-500 text-slate-400");
  if (!isAdmin) return <span className={cls} data-testid={`item-active-badge-${item.id}`}>{on ? "ACTIVE" : "INACTIVE"}</span>;
  const flip = async () => {
    try {
      const { data } = await api.patch(`/items/${item.id}/active`, { is_active: !on });
      onChanged(data);
      toast.success(`${item.name}: ${!on ? "ACTIVE — dropdown me dikhega" : "INACTIVE — naye orders me nahi dikhega"}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <button type="button" onClick={flip} className={cls + " hover:opacity-80"} title="Main Admin: Active / Inactive" data-testid={`item-active-toggle-${item.id}`}>
      {on ? "● ACTIVE" : "○ INACTIVE"}
    </button>
  );
}
